import { useState, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { MetricsCard } from '../components/portfolio/MetricsCard';
import { TooltipTerm } from '../components/ui/Tooltip';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { encryptionService } from '../services/encryptionService';
import { kucoinAPI, TradingBot, EarnPosition, VIPTier } from '../lib/api/kucoin-api';
import {
  useTradingBots,
  useEarnPositions,
  useVIPTier,
  useKucoinStats,
} from '../hooks/useKucoin';
import { BotPerformanceChart } from '../components/cryptocom/BotPerformanceChart';
import { StakingRewardsChart } from '../components/cryptocom/StakingRewardsChart';
import { BotFilters } from '../components/cryptocom/BotFilters';
import { ExportData } from '../components/cryptocom/ExportData';
import { APYComparisonChart } from '../components/cryptocom/APYComparisonChart';
// TODO: Create KuCoin-specific components
// import { VIPTierProgress } from '../components/kucoin/VIPTierProgress';
// import { VIPFeeSavingsCalculator } from '../components/kucoin/VIPFeeSavingsCalculator';
// import { SmartRecommendations } from '../components/kucoin/SmartRecommendations';

/**
 * KuCoin Dashboard - Trading & Earn Manager
 *
 * Features:
 * - Trading Bots (custom tracking - no native endpoint)
 * - KuCoin Earn (Flexible, Fixed, High-Yield)
 * - VIP Tier System (based on 30-day volume)
 * - Auto-refresh every 30-60s with React Query
 * - Intelligent caching
 * - Loading states & error handling
 *
 * CRITICAL DIFFERENCE vs Crypto.com:
 * - Requires PASSPHRASE (3rd auth parameter)
 * - No Card Benefits (replaced by VIP Tier system)
 * - Custom bot tracking (no native API endpoint)
 */

export function KucoinDashboard() {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [passphrase, setPassphrase] = useState(''); // ← CRITICAL: KuCoin requires passphrase
  const [useDemoMode, setUseDemoMode] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'bots' | 'earn' | 'vip'>('bots');
  const [filteredBots, setFilteredBots] = useState<TradingBot[]>([]);

  // Load saved credentials on mount
  useEffect(() => {
    const loadCredentials = async () => {
      const savedCreds = await encryptionService.getCredentials<{
        apiKey: string;
        apiSecret: string;
        passphrase: string;
        demoMode: boolean;
      }>('kucoin_credentials');

      if (savedCreds) {
        setApiKey(savedCreds.apiKey);
        setApiSecret(savedCreds.apiSecret);
        setPassphrase(savedCreds.passphrase);
        setUseDemoMode(savedCreds.demoMode ?? true);
        kucoinAPI.setCredentials({
          apiKey: savedCreds.apiKey,
          apiSecret: savedCreds.apiSecret,
          passphrase: savedCreds.passphrase,
          environment: 'production',
        });
        kucoinAPI.setDemoMode(savedCreds.demoMode ?? true);
        setIsConnected(true);
      }
    };

    loadCredentials();
  }, []);

  // React Query hooks - only fetch when connected
  const {
    data: tradingBots = [],
    isLoading: botsLoading,
    error: botsError,
  } = useTradingBots({
    enabled: isConnected,
  });

  const {
    data: earnPositions = [],
    isLoading: earnLoading,
    error: earnError,
  } = useEarnPositions({
    enabled: isConnected,
  });

  const {
    data: vipTier,
    isLoading: vipLoading,
    error: vipError,
  } = useVIPTier({
    enabled: isConnected,
  });

  // Get combined stats
  const {
    totalTradingProfit,
    totalEarnRewards,
    totalEarnValue,
    activeBots,
    totalEquity,
    totalSpot,
    avgEarnAPY,
  } = useKucoinStats();

  // Initialize filtered bots
  useEffect(() => {
    setFilteredBots(tradingBots);
  }, [tradingBots]);

  const getBotTypeColor = (type: TradingBot['type']) => {
    const colors = {
      SPOT_GRID: '#8b5cf6',
      DCA: '#10b981',
      FUTURES_GRID: '#3b82f6',
      INFINITY_GRID: '#f59e0b',
    };
    return colors[type];
  };

  const getBotTypeTooltip = (type: TradingBot['type']) => {
    const tooltips = {
      SPOT_GRID: 'Spot Grid: Place des ordres d\'achat et de vente à intervalles réguliers sur le marché spot',
      DCA: 'Dollar Cost Averaging: Achète automatiquement à intervalles réguliers pour lisser le prix',
      FUTURES_GRID: 'Futures Grid: Grid trading sur le marché perpétuel avec leverage',
      INFINITY_GRID: 'Infinity Grid: Grid adaptatif qui ajuste automatiquement les prix selon le marché',
    };
    return tooltips[type];
  };

  const getEarnTypeLabel = (type: EarnPosition['type']) => {
    const labels = {
      FLEXIBLE: 'Flexible',
      FIXED: 'Fixed Term',
      HIGH_YIELD: 'High-Yield',
    };
    return labels[type];
  };

  const getEarnTypeColor = (type: EarnPosition['type']) => {
    const colors = {
      FLEXIBLE: '#10b981',
      FIXED: '#3b82f6',
      HIGH_YIELD: '#8b5cf6',
    };
    return colors[type];
  };

  const handleConnect = async () => {
    if (!apiKey || !apiSecret || !passphrase) {
      setConnectionError('Veuillez remplir tous les champs (API Key, Secret, et Passphrase)');
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const credentials = {
        apiKey,
        apiSecret,
        passphrase,
        environment: 'production' as const,
      };

      // 🔐 SECURITY: Encrypt and save credentials in IndexedDB
      await encryptionService.setCredentials('kucoin_credentials', {
        apiKey,
        apiSecret,
        passphrase,
        demoMode: useDemoMode,
      });

      kucoinAPI.setCredentials(credentials);
      kucoinAPI.setDemoMode(useDemoMode);

      setIsConnected(true);
      setConnectionError(null);
    } catch (error) {
      console.error('Connection error:', error);
      setConnectionError(
        error instanceof Error ? error.message : 'Erreur de connexion. Vérifiez vos identifiants.'
      );
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    kucoinAPI.clearCredentials();
    await encryptionService.removeCredentials('kucoin_credentials');
    setIsConnected(false);
    setApiKey('');
    setApiSecret('');
    setPassphrase('');
    setConnectionError(null);
  };

  const handleFilterChange = (filtered: TradingBot[]) => {
    setFilteredBots(filtered);
  };

  // Connection form
  if (!isConnected) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.TrendingUp size={28} />
          Connect to KuCoin
        </h2>

        <div style={{
          padding: '16px',
          background: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '8px',
          marginBottom: '24px',
          borderLeft: '3px solid #3b82f6'
        }}>
          <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
            <strong>🔐 Connexion API Sécurisée</strong>
          </p>
          <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>
            Connectez votre compte KuCoin via API pour accéder à vos bots de trading, positions KuCoin Earn et VIP tier.
            Vos clés sont stockées localement et ne sont jamais envoyées à nos serveurs.
          </p>
        </div>

        {connectionError && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            borderLeft: '3px solid #ef4444',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icons.AlertCircle size={18} style={{ color: '#ef4444' }} />
            <span style={{ fontSize: '13px', color: '#ef4444' }}>{connectionError}</span>
          </div>
        )}

        {/* Demo/Live Mode Switch */}
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: useDemoMode ? 'rgba(245, 158, 11, 0.05)' : 'rgba(16, 185, 129, 0.05)',
          borderRadius: '8px',
          border: `1px solid ${useDemoMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`,
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}>
            <input
              type="checkbox"
              checked={useDemoMode}
              onChange={(e) => setUseDemoMode(e.target.checked)}
              disabled={isConnecting}
              style={{
                cursor: isConnecting ? 'not-allowed' : 'pointer',
                width: '18px',
                height: '18px',
                accentColor: 'var(--accent)'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: '4px' }}>
                {useDemoMode ? '🎭 Mode Démo' : '🔴 Mode Live (API Réelle)'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.5' }}>
                {useDemoMode
                  ? 'Utilisez des données fictives pour tester l\'interface (pas besoin de vraies clés)'
                  : 'Connectez-vous avec vos vraies clés API KuCoin pour voir vos données réelles'}
              </div>
            </div>
            {!useDemoMode && (
              <span style={{
                padding: '4px 8px',
                background: '#10b981',
                color: 'white',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                LIVE
              </span>
            )}
          </label>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            API Key
            <TooltipTerm content="Votre clé API KuCoin Exchange. Créez-la dans Account → API Management">
              <Icons.Info size={14} style={{ color: 'var(--muted)' }} />
            </TooltipTerm>
          </label>
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Votre clé API KuCoin"
            disabled={isConnecting}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            API Secret
            <TooltipTerm content="Votre clé secrète API. Conservez-la en sécurité et ne la partagez jamais">
              <Icons.Info size={14} style={{ color: 'var(--muted)' }} />
            </TooltipTerm>
          </label>
          <input
            type="password"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            placeholder="Votre clé secrète API"
            disabled={isConnecting}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            API Passphrase
            <TooltipTerm content="La passphrase est un paramètre d'authentification supplémentaire requis par KuCoin. Vous l'avez définie lors de la création de votre API Key.">
              <Icons.Info size={14} style={{ color: 'var(--muted)' }} />
            </TooltipTerm>
          </label>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Votre passphrase API"
            disabled={isConnecting}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>

        <button
          onClick={handleConnect}
          disabled={!apiKey || !apiSecret || !passphrase || isConnecting}
          style={{
            width: '100%',
            padding: '12px',
            background: isConnecting ? 'var(--muted)' : 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: (apiKey && apiSecret && passphrase && !isConnecting) ? 'pointer' : 'not-allowed',
            opacity: (apiKey && apiSecret && passphrase && !isConnecting) ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isConnecting ? (
            <>
              <Icons.Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Connexion en cours...
            </>
          ) : (
            <>
              <Icons.Activity size={18} />
              Se connecter à KuCoin
            </>
          )}
        </button>

        <div style={{ marginTop: '24px', padding: '12px', background: 'var(--bg)', borderRadius: '6px' }}>
          <p style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '8px' }}>
            <strong>ℹ️ Comment obtenir vos clés API ?</strong>
          </p>
          <ol style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '8px', marginLeft: '16px' }}>
            <li>Connectez-vous à KuCoin</li>
            <li>Allez dans Account → API Management</li>
            <li>Créez une nouvelle API Key avec passphrase</li>
            <li>Activez les permissions "General" et "Trade" (pas "Withdraw")</li>
            <li>Copiez votre API Key, Secret et Passphrase</li>
          </ol>
          <p style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '8px' }}>
            <strong>Note:</strong> Le mode Live utilise un backend proxy Vercel pour contourner les restrictions CORS de l'API KuCoin.
          </p>
          <button
            onClick={() => window.open('https://docs.kucoin.com/#authentication', '_blank')}
            style={{
              marginTop: '8px',
              background: 'transparent',
              border: 'none',
              color: 'var(--accent)',
              fontSize: '12px',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0
            }}
          >
            📖 Documentation API KuCoin →
          </button>
        </div>
      </div>
    );
  }

  // Loading state - show loading spinner when initially fetching data after connection
  const isInitialLoading = isConnected && (botsLoading || earnLoading || vipLoading);

  if (isInitialLoading) {
    return <LoadingSpinner size="lg" message="Chargement de vos données KuCoin..." />;
  }

  return (
    <>
      {/* Header with Connection Status */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icons.TrendingUp size={32} />
            <div>
              <h1 style={{ fontSize: '24px', marginBottom: '4px' }}>
                KuCoin Dashboard
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: useDemoMode ? '#f59e0b' : '#10b981',
                  animation: 'pulse 2s infinite'
                }}></div>
                <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                  {useDemoMode ? 'Mode Démo • Données fictives' : 'Connecté • API Live'}
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {vipTier && (
              <div style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Icons.Star size={16} />
                <span>VIP {vipTier.level}</span>
                <span style={{ fontSize: '11px', opacity: 0.9 }}>
                  {vipTier.makerFee}% / {vipTier.takerFee}%
                </span>
              </div>
            )}
            <button
              onClick={handleDisconnect}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text)',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Icons.X size={14} />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Demo Mode Banner */}
      {useDemoMode && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '8px',
          borderLeft: '4px solid #f59e0b'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Icons.Info size={20} style={{ color: '#f59e0b', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#f59e0b' }}>
                Mode Démo Actif
              </p>
              <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--muted)', marginBottom: '8px' }}>
                Vous visualisez des <strong>données fictives</strong> pour tester l'interface.
                L'API KuCoin ne peut pas être appelée directement depuis le navigateur pour des raisons de sécurité (CORS).
              </p>
              <p style={{ fontSize: '12px', lineHeight: '1.6', color: 'var(--muted)' }}>
                <strong>Pour utiliser vos vraies données:</strong> Déployez un backend proxy (ex: Vercel serverless function)
                qui fera le pont entre le frontend et l'API KuCoin.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="metrics" style={{ marginBottom: '24px' }}>
        <MetricsCard
          label="Total Equity"
          value={`$${totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color="green"
          icon={Icons.Wallet}
          tooltip="Valeur totale de votre compte KuCoin (Spot + Earn)"
        />
        <MetricsCard
          label="Total Trading Profit"
          value={`$${totalTradingProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color="blue"
          icon={Icons.TrendingUp}
          tooltip="Profit combiné de tous vos bots de trading actifs"
        />
        <MetricsCard
          label="Active Bots"
          value={activeBots}
          color="purple"
          icon={Icons.Bot}
          tooltip="Nombre de bots de trading actuellement actifs"
        />
        <MetricsCard
          label="Total Earn Value"
          value={`$${totalEarnValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          color="orange"
          icon={Icons.PiggyBank}
          tooltip="Montant total investi dans KuCoin Earn"
        />
      </div>

      {/* Export Data */}
      <ExportData bots={tradingBots} positions={earnPositions} />

      {/* Tab Navigation */}
      <div style={{ marginBottom: '24px', borderBottom: '2px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {[
            { id: 'bots' as const, label: 'Trading Bots', icon: Icons.Bot },
            { id: 'earn' as const, label: 'KuCoin Earn', icon: Icons.PiggyBank },
            { id: 'vip' as const, label: `VIP Tier ${vipTier?.level || 0}`, icon: Icons.Star }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom: selectedTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                color: selectedTab === tab.id ? 'var(--accent)' : 'var(--muted)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '-2px'
              }}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trading Bots Tab */}
      {selectedTab === 'bots' && (
        <>
          {/* Bot Performance Chart */}
          {tradingBots.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <BotPerformanceChart bots={tradingBots} />
            </div>
          )}

          <div className="card">
            <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.Bot size={24} />
              Trading Bots
              {tradingBots.length > 0 && (
                <span style={{
                  fontSize: '14px',
                  color: 'var(--muted)',
                  fontWeight: 'normal'
                }}>
                  ({filteredBots.length}/{tradingBots.length})
                </span>
              )}
            </h2>

            {tradingBots.length === 0 ? (
              <EmptyState
                icon={Icons.Bot}
                title="Aucun bot de trading actif"
                description="Vous n'avez pas encore de bots de trading configurés sur KuCoin Exchange"
                action={{
                  label: 'Ouvrir KuCoin Exchange',
                  onClick: () => window.open('https://www.kucoin.com/trade', '_blank'),
                  icon: Icons.ExternalLink
                }}
                tips={[
                  'Les Spot Grid bots sont parfaits pour les marchés latéraux',
                  'DCA réduit le risque en lissant votre prix d\'entrée',
                  'Infinity Grid s\'adapte automatiquement aux mouvements du marché'
                ]}
                helpLink={{
                  label: 'En savoir plus sur les bots de trading KuCoin',
                  onClick: () => window.open('https://www.kucoin.com/support/360039514032-Introduction-to-Trading-Bots', '_blank')
                }}
              />
            ) : (
              <>
                {/* Bot Filters */}
                <BotFilters bots={tradingBots} onFilterChange={setFilteredBots} />

                {filteredBots.length === 0 ? (
                  <div style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: 'var(--muted)'
                  }}>
                    <Icons.Filter size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                    <p style={{ fontSize: '14px' }}>Aucun bot ne correspond à vos filtres</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {filteredBots.map(bot => (
                      <div
                        key={bot.id}
                        style={{
                          padding: '16px',
                          background: 'var(--bg)',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${getBotTypeColor(bot.type)}`
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontSize: '16px', fontWeight: 600 }}>{bot.symbol}</span>
                              <TooltipTerm content={getBotTypeTooltip(bot.type)}>
                                <span style={{
                                  padding: '2px 8px',
                                  background: getBotTypeColor(bot.type),
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  cursor: 'help'
                                }}>
                                  {bot.type.replace('_', ' ')}
                                </span>
                              </TooltipTerm>
                              <span style={{
                                padding: '2px 8px',
                                background: bot.status === 'ACTIVE' ? '#10b981' : '#6b7280',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600
                              }}>
                                {bot.status}
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                              Démarré il y a {Math.floor((Date.now() - bot.createdAt) / (1000 * 60 * 60 * 24))} jours
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '18px', fontWeight: 600, color: bot.stats.profit >= 0 ? '#10b981' : '#ef4444' }}>
                              ${bot.stats.profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ fontSize: '13px', color: bot.stats.profitPercentage >= 0 ? '#10b981' : '#ef4444' }}>
                              {bot.stats.profitPercentage >= 0 ? '+' : ''}{bot.stats.profitPercentage.toFixed(2)}%
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Trades</div>
                            <div style={{ fontSize: '14px', fontWeight: 600 }}>{bot.stats.trades}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>
                              <TooltipTerm content="Taux de réussite des trades du bot">
                                Win Rate
                              </TooltipTerm>
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: bot.stats.winRate >= 60 ? '#10b981' : bot.stats.winRate >= 50 ? '#f59e0b' : '#ef4444' }}>
                              {bot.stats.winRate.toFixed(1)}%
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Avg Trade</div>
                            <div style={{ fontSize: '14px', fontWeight: 600 }}>
                              ${(bot.stats.profit / bot.stats.trades).toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                          <button style={{
                            padding: '6px 12px',
                            background: 'var(--accent)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icons.Eye size={14} />
                            Détails
                          </button>
                          <button style={{
                            padding: '6px 12px',
                            background: 'transparent',
                            color: 'var(--text)',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icons.ExternalLink size={14} />
                            Ouvrir sur KuCoin
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* KuCoin Earn Tab */}
      {selectedTab === 'earn' && (
        <>
          {/* Staking Rewards Chart */}
          {earnPositions.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <StakingRewardsChart positions={earnPositions} />
            </div>
          )}

          {/* APY Comparison Chart */}
          {earnPositions.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <APYComparisonChart positions={earnPositions} />
            </div>
          )}

          <div className="card">
            <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.PiggyBank size={24} />
              Positions KuCoin Earn
            </h2>

            {earnPositions.length === 0 ? (
              <EmptyState
                icon={Icons.PiggyBank}
                title="Aucune position KuCoin Earn"
                description="Vous n'avez pas encore de positions KuCoin Earn actives"
                action={{
                  label: 'Découvrir KuCoin Earn',
                  onClick: () => window.open('https://www.kucoin.com/earn', '_blank'),
                  icon: Icons.ExternalLink
                }}
                tips={[
                  'KuCoin Earn offre jusqu\'à 20% APY',
                  'Flexible Savings: Retraits instantanés sans frais',
                  'Fixed Term: APY plus élevés avec période de blocage'
                ]}
              />
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {earnPositions.map(position => (
                  <div
                    key={position.id}
                    style={{
                      padding: '16px',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      borderLeft: '4px solid #8b5cf6'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '16px', fontWeight: 600 }}>{position.currency}</span>
                          <span style={{
                            padding: '2px 8px',
                            background: getEarnTypeColor(position.type),
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600
                          }}>
                            {getEarnTypeLabel(position.type)}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          {position.amount.toLocaleString()} {position.currency} investis
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#10b981' }}>
                          <TooltipTerm content="Annual Percentage Yield - Rendement annuel avec intérêts composés">
                            {position.apy.toFixed(2)}% APY
                          </TooltipTerm>
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                          <TooltipTerm content="Annual Percentage Rate - Taux d'intérêt annuel simple">
                            {position.apr.toFixed(2)}% APR
                          </TooltipTerm>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Total Rewards</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                          +{position.totalRewards.toFixed(6)} {position.currency}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Pending Rewards</div>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>
                          +{position.pendingRewards.toFixed(6)} {position.currency}
                        </div>
                      </div>
                      {position.lockupDays && (
                        <div>
                          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>
                            <TooltipTerm content="Durée pendant laquelle vos tokens sont bloqués">
                              Lockup Period
                            </TooltipTerm>
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 600 }}>
                            {position.lockupDays} jours
                          </div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Status</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                          {position.status}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                      <button style={{
                        padding: '6px 12px',
                        background: 'var(--accent)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Icons.Eye size={14} />
                        Détails
                      </button>
                      <button style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Icons.ExternalLink size={14} />
                        Ouvrir sur KuCoin
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* VIP Tier Tab */}
      {selectedTab === 'vip' && (
        <>
          {vipTier ? (
            <>
                        {/* Current VIP Tier */}
                        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-8 text-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-3xl font-bold mb-2">VIP Tier {vipTier.level}</h3>
                              <p className="text-yellow-100 text-lg">
                                Volume 30j: ${vipTier.tradingVolume30d.toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-sm opacity-90 mb-1">Fees</div>
                              <div className="text-2xl font-bold">
                                {vipTier.makerFee}% / {vipTier.takerFee}%
                              </div>
                              <div className="text-sm opacity-90">Maker / Taker</div>
                            </div>
                          </div>
                        </div>

                        {/* Next Tier Progress */}
                        {vipTier.nextTier && (
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                              Progression vers VIP {vipTier.nextTier.level}
                            </h3>
                            <div className="space-y-4">
                              <div>
                                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  <span>Volume requis</span>
                                  <span className="font-bold text-gray-900 dark:text-white">
                                    ${vipTier.nextTier.volumeNeeded.toLocaleString()} restant
                                  </span>
                                </div>
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-blue-500 to-green-500"
                                    style={{
                                      width: `${Math.min(
                                        ((vipTier.tradingVolume30d - vipTier.requiredVolume) /
                                          (vipTier.nextTier.requiredVolume - vipTier.requiredVolume)) *
                                          100,
                                        100
                                      )}%`,
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4 mt-6">
                                <div>
                                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                    Nouveau Maker Fee
                                  </div>
                                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {vipTier.nextTier.makerFee}%
                                  </div>
                                  <div className="text-sm text-green-600">
                                    {vipTier.makerFee > vipTier.nextTier.makerFee && '-'}
                                    {Math.abs(vipTier.makerFee - vipTier.nextTier.makerFee)}%
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                    Nouveau Taker Fee
                                  </div>
                                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {vipTier.nextTier.takerFee}%
                                  </div>
                                  <div className="text-sm text-green-600">
                                    {vipTier.takerFee > vipTier.nextTier.takerFee && '-'}
                                    {Math.abs(vipTier.takerFee - vipTier.nextTier.takerFee)}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* TODO: Add VIP Tier Progress & Fee Savings Calculator components */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            💡 <strong>À venir :</strong> Composants VIPTierProgress et VIPFeeSavingsCalculator
                          </p>
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        icon={Icons.Star}
                        title="Chargement des informations VIP..."
                        description="Récupération de votre tier VIP depuis l'API KuCoin"
                      />
                    )}
        </>
      )}

      <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </>
    );
  }
